import { ItemSettingsService } from '@supply-mover-2-0/core';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { getTenantAlias } from '../lib/tenantResolver';
import { NotFoundPage } from '../pages/NotFoundPage';
import { fetchPortalSite } from '../services/portalSiteService';
import { usePortalSiteStore } from '../stores/portalSiteStore';

interface TenantAuthOptions {
	phoneLoginEnabled: boolean;
}

interface TenantContext {
	alias: string;
	name: string;
	authOptions: TenantAuthOptions;
}

const TenantCtx = createContext<TenantContext | null>(null);

/** Access the current validated tenant alias + name. */
export function useTenant(): TenantContext {
	const ctx = useContext(TenantCtx);
	if (ctx == null) {
		throw new Error('useTenant must be used inside TenantGate');
	}
	return ctx;
}

interface TenantGateProps {
	children: ReactNode;
}

/**
 * Validates the tenant + brand from the current subdomain before rendering
 * children, using a single bootstrap call to `GET /api/portal/site`.
 *
 * The bootstrap endpoint resolves everything the browser needs to render
 * its first frame: tenant alias/name, brand alias/displayName/logoUrl,
 * portalSiteId (threaded into subsequent login + API calls), and the
 * tenant-level auth options. It 404s when the tenant/brand isn't
 * configured; that's treated as the not-found state, not an error.
 *
 * Three outcomes:
 * 1. No alias in URL (bare domain) → 404 page
 * 2. Tenant/brand not configured → 404 page
 * 3. Valid → renders children with TenantContext available
 */
export function TenantGate({ children }: TenantGateProps): JSX.Element {
	const alias = useMemo(() => getTenantAlias(), []);
	const setSite = usePortalSiteStore((s) => s.setSite);
	const setNotConfigured = usePortalSiteStore((s) => s.setNotConfigured);

	const {
		data: bootstrap,
		isLoading: siteLoading,
		isError: siteError,
	} = useQuery({
		queryKey: ['portal-site-bootstrap'],
		queryFn: fetchPortalSite,
		retry: false,
		staleTime: Infinity,
	});

	useEffect(() => {
		if (bootstrap == null) {
			return;
		}

		if (bootstrap.notConfigured) {
			setNotConfigured();
			return;
		}

		setSite(bootstrap.site);

		// Seed the global ItemSettingsService with the tenant's
		// `maxPriceDecimalPrecision` from the bootstrap. This drives
		// `formatPrice()` in libs/core, which the order detail page
		// uses for unit price rendering — without this seed every
		// tenant would show the default 2 decimals regardless of what
		// they have configured.
		if (bootstrap.site?.maxPriceDecimalPrecision != null) {
			ItemSettingsService.setMaxPriceDecimalPrecision(
				bootstrap.site.maxPriceDecimalPrecision,
			);
		}

		// Reflect the brand in the browser tab title — small UX win that
		// makes a multi-brand tenant feel like distinct sites at a glance.
		if (bootstrap.site?.displayName != null) {
			document.title = bootstrap.site.displayName;
		}
	}, [bootstrap, setNotConfigured, setSite]);

	// No alias in URL — bare domain like local.portal.workd.com
	if (alias == null) {
		return <NotFoundPage />;
	}

	if (siteLoading) {
		// Don't paint our own bg here — index.html's <style> block
		// already paints html+body in `var(--brand-primary, #0F1A2A)`,
		// so an empty min-h-dvh shell lets the brand primary show
		// through during the bootstrap fetch. This kills the white /
		// gray-950 flash that used to show before the tenant resolved
		// (the "Workd Portal black screen" the QA pass flagged).
		return <div className="min-h-dvh" />;
	}

	if (
		siteError ||
		bootstrap == null ||
		bootstrap.notConfigured ||
		bootstrap.site == null
	) {
		return <NotFoundPage />;
	}

	// Valid tenant + resolved brand — provide context and render the app.
	// The brand's displayName takes precedence over the tenant's name so
	// multi-brand tenants show the right label; authOptions flow through
	// from the same bootstrap response so the login form knows which
	// methods to render without a second round trip.
	return (
		<TenantCtx.Provider
			value={{
				alias,
				name: bootstrap.site.displayName,
				authOptions: bootstrap.site.authOptions,
			}}
		>
			{children}
		</TenantCtx.Provider>
	);
}
